import * as Y from "yjs";
import { entityTypes, type EntityByType, type EntityType } from "../schemas";
import { entityMap, jsonEqual, parseEntity, quickEntity, type Snapshot } from "./doc";

/**
 * Keeps an immutable snapshot of the document up to date by re-parsing only the entities
 * that changed. Each change produces new Map objects for the changed types, so React can
 * compare by reference.
 */
export interface SnapshotCacheOptions {
  /**
   * Show changed entities from a cheap structural read at once and run full validation
   * shortly after (the web app's cold start, SPEC §11.4). The server validates eagerly.
   */
  deferValidation?: boolean;
  /** How the deferred validation is scheduled (tests pass a synchronous scheduler). */
  schedule?: (run: () => void) => void;
}

export class SnapshotCache {
  private current: Snapshot;
  private readonly listeners = new Set<() => void>();
  private readonly unobserve: (() => void)[] = [];
  private readonly pending = new Map<EntityType, Set<string>>();
  private validationScheduled = false;

  constructor(
    private readonly doc: Y.Doc,
    private readonly options: SnapshotCacheOptions = {},
  ) {
    const initial = {} as Record<EntityType, Map<string, unknown>>;
    for (const type of entityTypes) {
      const map = new Map<string, unknown>();
      entityMap(doc, type).forEach((value, id) => {
        const e = value instanceof Y.Map ? parseEntity(type, value.toJSON()) : null;
        if (e) {
          map.set(id, e);
        }
      });
      initial[type] = map;
      const top = entityMap(doc, type);
      const handler = (events: Y.YEvent<Y.Map<unknown>>[]) => this.onEvents(type, events);
      top.observeDeep(handler);
      this.unobserve.push(() => top.unobserveDeep(handler));
    }
    this.current = initial as unknown as Snapshot;
  }

  get snapshot(): Snapshot {
    return this.current;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  destroy(): void {
    for (const u of this.unobserve) {
      u();
    }
    this.listeners.clear();
  }

  private onEvents(type: EntityType, events: Y.YEvent<Y.Map<unknown>>[]): void {
    const top = entityMap(this.doc, type);
    const dirty = new Set<string>();
    for (const event of events) {
      if (event.target === top) {
        for (const key of event.changes.keys.keys()) {
          dirty.add(key);
        }
      } else {
        const id = event.path[0];
        if (typeof id === "string") {
          dirty.add(id);
        }
      }
    }
    if (dirty.size === 0) {
      return;
    }
    const defer = this.options.deferValidation === true;
    const next = new Map(this.current[type] as Map<string, EntityByType[typeof type]>);
    for (const id of dirty) {
      const value = top.get(id);
      const raw = value instanceof Y.Map ? value.toJSON() : null;
      const e =
        raw === null ? null : ((defer ? quickEntity(type, raw) : null) ?? parseEntity(type, raw));
      if (e) {
        next.set(id, e);
      } else {
        next.delete(id);
      }
    }
    this.current = { ...this.current, [type]: next };
    if (defer) {
      const ids = this.pending.get(type) ?? new Set<string>();
      for (const id of dirty) {
        ids.add(id);
      }
      this.pending.set(type, ids);
      this.scheduleValidation();
    }
    for (const l of this.listeners) {
      l();
    }
  }

  private scheduleValidation(): void {
    if (this.validationScheduled) {
      return;
    }
    this.validationScheduled = true;
    const schedule = this.options.schedule ?? ((run: () => void) => setTimeout(run, 50));
    schedule(() => this.validatePending());
  }

  /** Fully validates entities shown from a quick read; drops invalid ones, applies defaults. */
  validatePending(): void {
    this.validationScheduled = false;
    let changed = false;
    for (const [type, ids] of this.pending) {
      const top = entityMap(this.doc, type);
      const current = this.current[type] as Map<string, unknown>;
      let next: Map<string, unknown> | null = null;
      for (const id of ids) {
        const value = top.get(id);
        const parsed = value instanceof Y.Map ? parseEntity(type, value.toJSON()) : null;
        const shown = current.get(id);
        if (parsed === null ? shown !== undefined : !jsonEqual(parsed, shown)) {
          next ??= new Map(current);
          if (parsed) {
            next.set(id, parsed);
          } else {
            next.delete(id);
          }
        }
      }
      if (next) {
        this.current = { ...this.current, [type]: next };
        changed = true;
      }
    }
    this.pending.clear();
    if (changed) {
      for (const l of this.listeners) {
        l();
      }
    }
  }
}
